import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useLazyQuery } from '@apollo/client';
import { GET_SERIES_CLASSIFICATION } from 'modules/Apollo/queries';
import { steppedHPConfigurations, clinicalStepsBaseline, makeVersionedSteps } from '../state';
import type { VersionedStepConfigurations } from '../state';
import type { GetSeriesClassificationQuery } from 'generated/graphql';
import { flattenSeriesClassification } from '../../Viewer/StudiesBar/HangingProtocols/HPv4/hangingProtocolFormHelpers';

type ClassifyRequest = {
  viewportId: string;
  stepIndex: number;
  seriesDescription: string;
  studyDescription?: string;
};

type PendingClassification = {
  stepIndex: number;
  abort: AbortController;
};

export function useClinicalViewportClassification() {
  // `pendingClassificationsCount` is a React state counter so components re-render when
  // the in-flight count changes. `pendingRef` is a ref (not state) because the Map must
  // be mutated synchronously in the same microtask as the fetch, and making it state
  // would cause a stale-closure read of the old Map on rapid consecutive classify() calls.
  const [pendingClassificationsCount, setPendingClassificationsCount] = useState(0);
  const pendingRef = useRef<Map<string, PendingClassification>>(new Map());
  const setSteppedHP = useSetRecoilState(steppedHPConfigurations);
  const setBaseline = useSetRecoilState(clinicalStepsBaseline);

  const [fetchClassification] = useLazyQuery<GetSeriesClassificationQuery>(
    GET_SERIES_CLASSIFICATION,
    {
      context: { telemetry: { component: 'useClinicalViewportClassification' } },
    }
  );

  const classify = useCallback(
    (request: ClassifyRequest) => {
      const { viewportId, stepIndex, seriesDescription, studyDescription } = request;
      const key = `${stepIndex}:${viewportId}`;

      // Cancel existing classification for this viewport
      const existing = pendingRef.current.get(key);
      if (existing) {
        existing.abort.abort();
        pendingRef.current.delete(key);
        setPendingClassificationsCount((n) => Math.max(0, n - 1));
      }

      const abortController = new AbortController();
      pendingRef.current.set(key, { stepIndex, abort: abortController });
      setPendingClassificationsCount((n) => n + 1);

      fetchClassification({
        variables: {
          dicomSeriesDescription: seriesDescription,
          dicomStudyDescription: studyDescription ?? undefined,
        },
        context: { fetchOptions: { signal: abortController.signal } },
      })
        .then((result) => {
          if (abortController.signal.aborted) return;

          const classification = result.data?.seriesClassification?.classification;
          if (classification) {
            const criteria = flattenSeriesClassification(classification).map((c) => ({
              category: c.category,
              value: c.value,
            }));

            setSteppedHP((prev: VersionedStepConfigurations) => {
              const steps = prev.steps?.map((step, i) => {
                if (i !== stepIndex) return step;
                const viewport = step.viewports?.[viewportId];
                // The viewport must still hold the series this classification was requested for.
                //
                // These fetches outlive the drag that started them. If the step is reverted while
                // one is in flight -- confirming the Add Step discard, "Reset to original state",
                // Reset All -- this used to stamp the old series' criteria onto the restored
                // viewport. Every other field came back from the baseline, so the step differed
                // from its baseline by `criteria` alone and read dirty forever: the yellow dot
                // could not be cleared by reverting again, because reverting is what triggered it.
                //
                // Measured on Phase 4f2 of the comprehensive live spec: after the revert the only
                // difference was VIEW_ORIENTATION "27T" (the dragged series) against "2ZL" (the
                // restored one).
                const heldSeries = viewport?.series_criteria_source_text ?? viewport?.display_name;
                if (viewport == null || heldSeries !== seriesDescription) return step;
                return {
                  ...step,
                  viewports: {
                    ...step.viewports,
                    [viewportId]: {
                      ...step.viewports[viewportId],
                      criteria,
                    },
                  },
                };
              });
              return makeVersionedSteps(steps ?? null, prev);
            });

            // Mirror the criteria into the baseline, but only onto a baseline viewport already
            // holding this same series.
            //
            // Criteria are derived from the series by the server; they are not something the user
            // typed, so their arrival must never be what makes a step dirty. Saving a step while its
            // classification is still in flight baselines it without criteria, and the response then
            // lands and mutates the step -- leaving it different from the baseline it was just saved
            // against, with a dirty dot the user cannot clear because saving again just repeats the
            // race. Measured as a step going clean on save and dirty again 20ms later, with no
            // baseline write in between.
            //
            // The identity check is what keeps this honest. If the user has dragged a new series and
            // not saved, the baseline still describes the old one, and writing these criteria onto it
            // would fabricate a baseline that never existed -- old series, new criteria -- and hide a
            // genuine unsaved change. In that case the baseline is left alone and the step stays
            // correctly dirty.
            setBaseline((prev) => {
              if (prev == null) return prev;
              const target = prev[stepIndex];
              const baselineViewport = target?.viewports?.[viewportId];
              if (baselineViewport == null) return prev;
              const baselineSeries =
                baselineViewport.series_criteria_source_text ?? baselineViewport.display_name;
              if (baselineSeries !== seriesDescription) return prev;
              return prev.map((step, i) =>
                i !== stepIndex
                  ? step
                  : {
                      ...step,
                      viewports: {
                        ...step.viewports,
                        [viewportId]: { ...step.viewports[viewportId], criteria },
                      },
                    }
              );
            });
          }
        })
        .catch(() => {
          // Classification failed — viewport saves without criteria
        })
        .finally(() => {
          // Skip cleanup if cancelled or superseded — the replacement manages its own counter.
          if (!abortController.signal.aborted) {
            pendingRef.current.delete(key);
            setPendingClassificationsCount((n) => Math.max(0, n - 1));
          }
        });
    },
    [fetchClassification, setSteppedHP, setBaseline]
  );

  const cancelForStep = useCallback((stepIndex: number) => {
    for (const [key, pending] of pendingRef.current) {
      if (pending.stepIndex === stepIndex) {
        pending.abort.abort();
        pendingRef.current.delete(key);
        setPendingClassificationsCount((n) => Math.max(0, n - 1));
      }
    }
  }, []);

  const cancelAll = useCallback(() => {
    for (const [, pending] of pendingRef.current) {
      pending.abort.abort();
    }
    pendingRef.current.clear();
    setPendingClassificationsCount(0);
  }, []);

  // Cancel all in-flight requests on unmount to prevent stale Recoil writes
  // after the component has been removed from the tree.
  useEffect(() => () => cancelAll(), [cancelAll]);

  return {
    classify,
    pendingClassifications: pendingClassificationsCount,
    cancelForStep,
    cancelAll,
  };
}
